import React, { useState, useEffect, useRef } from 'react';
import { isPurchaseConfirmed, isPromoSwitchFinished, canContinueWithExistingPlan, PLAN_RANK } from '../utils/purchaseConfirmation';
import { beginPromoSwitch, prepareCheckout } from '../utils/api';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useBiqPageContext } from '../context/BiqPageContext';
import PaywallCard from './components/paywall/PaywallCard';
import PromoCodeInput from './components/paywall/PromoCodeInput';
import PaywallNotice from './components/paywall/PaywallNotice';
import PageSkeleton from '../components/PageSkeleton';
import { getOfferings, purchasePackage, getActiveWebSubscription } from '../services/revenuecatService';
import { useTierFeatures } from '../hooks/useTierFeatures';
import { useSubscriptionDetails, useCancelSubscription } from '../utils/api-hooks/subscription';

const getOfferingPlansMetadata = (offering) => {
  const raw = offering?.metadata?.plans;
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
};

const TIER_RANK = PLAN_RANK;

// Scheduled Elite -> Advanced downgrade (charged only when Elite ends). Off unless enabled.
const SCHEDULED_DOWNGRADE_ENABLED = import.meta.env.VITE_ENABLE_SCHEDULED_DOWNGRADE === 'true';

const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '');

// Best-effort: re-assert RevenueCat $email to the signed-in account email.
// Latency-capped; failures must not block purchase or activation.
const seedAccountEmail = () =>
  Promise.race([
    prepareCheckout(),
    new Promise((resolve) => setTimeout(resolve, 3000)),
  ]).catch(() => null);

const resolveFeatureText = (feature, language) => {
  const lang = (language || 'en').split('-')[0];
  if (typeof feature === 'string') return feature;
  const text = feature?.text;
  if (text && typeof text === 'object') {
    return text[lang] || text.en || '';
  }
  if (typeof text === 'string') return text;
  if (typeof feature?.name === 'string') return feature.name;
  if (typeof feature?.label === 'string') return feature.label;
  return '';
};

const Paywall = () => {
  const { t, i18n } = useTranslation();
  const { tier, email, refetchSubscription, refetchUserDetails } = useUser();
  const { setPageExtras } = useBiqPageContext() || {};
  const { getPlanMetadata } = useTierFeatures();
  const [offerings, setOfferings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  // In-app status dialog. A native alert() freezes the page before React paints the new
  // plan, so the screen behind the popup still showed the old tier after a successful payment.
  const [notice, setNotice] = useState(null);
  // A payment went through but the plan is not confirmed yet: never offer another checkout.
  const [pendingPurchasePlan, setPendingPurchasePlan] = useState(null);
  // Promo + an existing paid plan: the plan the user chose to continue with (ends the promo only).
  const [promoEndPlan, setPromoEndPlan] = useState(null);
  const showNotice = (message, { kind = 'info' } = {}) => setNotice({ message, kind });
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showScheduleConfirm, setShowScheduleConfirm] = useState(false);
  const [promoSwitchPlan, setPromoSwitchPlan] = useState(null);
  const [promoSwitchDone, setPromoSwitchDone] = useState(false);
  const [webElite, setWebElite] = useState(null);
  const webEliteRequest = useRef(0);

  const {
    data: subscriptionData,
    isLoading: isLoadingSubscription,
  } = useSubscriptionDetails();

  const cancelSubscriptionMutation = useCancelSubscription();

  useEffect(() => {
    const fetchOfferings = async () => {
      try {
        setLoading(true);
        const currentOffering = await getOfferings();
        setOfferings(currentOffering);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch offerings:', err);
        setError(t('subscription.paywall.errorLoadingPlans'));
      } finally {
        setLoading(false);
      }
    };

    fetchOfferings()
        .then()
  }, []);

  // Elite web subscribers: read the portal link from the SDK and re-check state
  // whenever the user comes back from the billing portal.
  useEffect(() => {
    if (!SCHEDULED_DOWNGRADE_ENABLED || tier?.toLowerCase() !== 'elite') {
      setWebElite(null);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      const request = ++webEliteRequest.current;
      const sub = await getActiveWebSubscription('elite');
      if (!cancelled && request === webEliteRequest.current) setWebElite(sub);
    };
    const onFocus = () => {
      load();
      refetchSubscription();
    };
    load();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [tier]);

  useEffect(() => {
    if (!setPageExtras) return undefined;
    if (isLoadingSubscription && !subscriptionData) {
      setPageExtras(tier ? { tier } : null);
      return () => setPageExtras(null);
    }
    const expiresRaw =
      subscriptionData?.expires_at ?? subscriptionData?.expires_date ?? null;
    let expiresAt = null;
    if (typeof expiresRaw === 'string' && expiresRaw.trim()) {
      expiresAt = expiresRaw.trim();
    } else if (typeof expiresRaw === 'number' && Number.isFinite(expiresRaw)) {
      expiresAt = new Date(expiresRaw).toISOString();
    }
    setPageExtras({
      ...(tier ? { tier } : {}),
      ...(typeof subscriptionData?.is_promotional === 'boolean'
        ? { is_promotional: subscriptionData.is_promotional }
        : {}),
      ...(expiresAt ? { expires_at: expiresAt } : {}),
    });
    return () => setPageExtras(null);
  }, [setPageExtras, tier, subscriptionData, isLoadingSubscription]);

  // Polls the server for the new plan. A failed refresh is "not confirmed yet", never "payment failed".
  const waitForPurchaseConfirmation = async (planId, maxAttempts = 10, delayMs = 2000) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
      try {
        const [subResult] = await Promise.all([refetchSubscription(), refetchUserDetails()]);
        if (isPurchaseConfirmed(subResult?.data, planId)) return true;
      } catch (err) {
        console.warn('Purchase confirmation refresh failed:', err?.message);
      }
    }
    return false;
  };

  // Shows the outcome once the server has (or has not yet) reported the purchased plan.
  const finishPurchase = async (plan, attempts = 10) => {
    const confirmed = await waitForPurchaseConfirmation(plan.id, attempts);
    setProcessingMessage('');
    if (confirmed) {
      setPendingPurchasePlan(null);
      showNotice(t('subscription.paywall.subscriptionActivated'), { kind: 'success' });
      return;
    }
    setPendingPurchasePlan({ ...plan, pendingKind: 'purchase' });
    // Payment went through; the plan is still being activated on our side. This is never a
    // failure and must not invite a second purchase.
    showNotice(t('subscription.paywall.subscriptionPendingRefresh'), { kind: 'info' });
  };

  // The checkout lock ends when the SERVER confirms the operation that is pending (a plain purchase
  // or a promo switch), whichever refresh shows it: this reconciles against fresh subscription data
  // independently of the background polling below.
  const isPendingResolved = (data, pending) =>
    pending.pendingKind === 'switch'
      ? isPromoSwitchFinished(data, pending.id)
      : isPurchaseConfirmed(data, pending.id);

  useEffect(() => {
    if (pendingPurchasePlan && subscriptionData && isPendingResolved(subscriptionData, pendingPurchasePlan)) {
      setPendingPurchasePlan(null);
    }
  }, [pendingPurchasePlan, subscriptionData]);

  // While activation is pending, quietly re-check the server (no buttons). The refreshed data flows
  // into subscriptionData above, which releases the lock; a later refresh still does.
  useEffect(() => {
    if (!pendingPurchasePlan) return undefined;
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        await Promise.all([refetchSubscription(), refetchUserDetails()]);
      } catch (err) {
        console.warn('Pending purchase re-check failed:', err?.message);
      }
      if (attempts >= 36) clearInterval(timer);
    }, 5000);
    return () => clearInterval(timer);
  }, [pendingPurchasePlan]);

  useEffect(() => {
    // Once the server reports no promotional access, the session lock from a finished switch is
    // no longer needed; a promo redeemed later must be able to switch again.
    if (promoSwitchDone && subscriptionData && subscriptionData.is_promotional !== true) {
      setPromoSwitchDone(false);
    }
  }, [promoSwitchDone, subscriptionData]);

  // A plan ranked below the user's current plan. Only the Free plan can be reached
  // from here (it opens the cancel confirmation); a paid -> paid downgrade is not
  // offered in-app, so it never starts a second purchase on top of the current one.
  const isLowerThanCurrent = (plan) => {
    const currentRank = TIER_RANK[tier?.toLowerCase()];
    const targetRank = TIER_RANK[plan?.id];
    return currentRank !== undefined && targetRank !== undefined && currentRank > targetRank;
  };

  const isDowngradeTo = (plan) => plan?.id === 'free' && isLowerThanCurrent(plan);

  // Neutral label (button disabled) while the Free downgrade cannot be started.
  const getLockedLabel = (plan) => {
    if (pendingChange && plan.id === pendingChange.tier) {
      return t('subscription.paywall.scheduled');
    }
    if (!isDowngradeTo(plan)) return null;
    if (isLoadingSubscription) return t('subscription.paywall.loadingSubscription');
    return subscriptionData?.has_canceled === true
      ? t('subscription.paywall.cancellationScheduled')
      : null;
  };

  const webSubscription = subscriptionData?.web_subscription || null;
  const pendingChange = webSubscription?.pending_change || null;
  const periodEnd = webElite?.expiresDate || webSubscription?.current_period_ends_at || null;

  // The scheduled downgrade is offered only for a renewing, paid Web Billing Elite
  // subscription with no change already pending. Anything unknown fails closed.
  const canScheduleDowngrade =
    SCHEDULED_DOWNGRADE_ENABLED &&
    tier?.toLowerCase() === 'elite' &&
    Boolean(webElite?.managementURL) &&
    webElite.willRenew !== false &&
    Boolean(webSubscription) &&
    webSubscription.has_canceled !== true &&
    !pendingChange &&
    periodEnd !== null &&
    !Number.isNaN(new Date(periodEnd).getTime());

  // Promotional-only account (nothing paid to schedule), on any paid tier: the user may
  // pay for another plan (Advanced -> Elite, or Elite -> Advanced). Promotional access
  // is ended only AFTER that payment succeeds.
  const canSwitchFromPromo =
    Boolean(tier) &&
    !['free', 'ifpa'].includes(tier.toLowerCase()) &&
    subscriptionData?.is_promotional === true &&
    !webSubscription &&
    !promoSwitchDone;

  // A lower PAID plan keeps the red "Downgrade to ..." button but is disabled unless
  // one of the safe downgrade paths applies: starting a plain purchase here would add a
  // second subscription on top of a paid one.
  // Promo + a paid plan the user already has (e.g. a promo redeemed on top of a paid plan):
  // moving to that paid plan just ends the promo, no new payment.
  const isExistingPaidPlan = (plan) => canContinueWithExistingPlan(subscriptionData, plan.id);

  const isDowngradeUnavailable = (plan) =>
    isLowerThanCurrent(plan) &&
    plan.id !== 'free' &&
    !isExistingPaidPlan(plan) &&
    !(plan.id === 'advanced' && (canScheduleDowngrade || canSwitchFromPromo));

  useEffect(() => {
    if (!showScheduleConfirm && !promoSwitchPlan) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowScheduleConfirm(false);
        setPromoSwitchPlan(null);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showScheduleConfirm, promoSwitchPlan]);

  // The server ends the promotional access itself once RevenueCat confirms the payment. The
  // browser only watches: a failed or slow refresh is "not finished yet", never a failure.
  const waitForPromoSwitch = async (planId, maxAttempts = 10, delayMs = 2000) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
      try {
        const [subResult] = await Promise.all([refetchSubscription(), refetchUserDetails()]);
        if (isPromoSwitchFinished(subResult?.data, planId)) return true;
      } catch (err) {
        console.warn('Promo switch refresh failed:', err?.message);
      }
    }
    return false;
  };

  const finishPromoSwitch = async (plan, attempts = 10) => {
    const finished = await waitForPromoSwitch(plan.id, attempts);
    setProcessingMessage('');
    if (finished) {
      setPendingPurchasePlan(null);
      showNotice(t('subscription.paywall.promoSwitchSuccess', { plan: plan.name }), { kind: 'success' });
      return;
    }
    setPendingPurchasePlan({ ...plan, pendingKind: 'switch' });
    // Payment is safe; the server finishes the change on its own, even if this page is closed.
    showNotice(t('subscription.paywall.promoSwitchPartial', { plan: plan.name }), { kind: 'info' });
  };

  const handleEndPromoKeepPaid = async () => {
    const plan = promoEndPlan;
    setPromoEndPlan(null);
    if (!plan || purchasing) return;
    setPurchasing(true);
    setProcessingMessage(t('subscription.paywall.endingPromo'));
    try {
      const begun = await beginPromoSwitch(plan.id, undefined, { useExistingPlan: true });
      if (begun?.promo_switch?.state === 'done') {
        await Promise.all([refetchSubscription(), refetchUserDetails()]);
        showNotice(t('subscription.paywall.promoSwitchSuccess', { plan: plan.name }), { kind: 'success' });
      } else {
        // Only watches the server (no checkout is ever opened from this path).
        await finishPromoSwitch(plan);
      }
    } catch (err) {
      console.error('End promo error:', err);
      showNotice(err?.message || t('subscription.paywall.purchaseError'), { kind: 'error' });
    } finally {
      setPurchasing(false);
      setProcessingMessage('');
    }
  };

  const handlePromoSwitch = async () => {
    const plan = promoSwitchPlan;
    setPromoSwitchPlan(null);
    // Re-check at the moment of confirmation: a refresh may have shown a paid plan.
    if (!plan?.rcPackage || purchasing || pendingPurchasePlan || !canSwitchFromPromo) return;

    setPurchasing(true);
    setProcessingMessage(t('subscription.paywall.processingPayment'));
    try {
      // Record the switch on the server first. If that fails nothing is charged.
      const begun = await beginPromoSwitch(
        plan.id,
        plan.rcPackage.product?.identifier || plan.rcPackage.rcBillingProduct?.identifier
      );
      if (begun?.promo_switch?.state === 'revoking') {
        // A payment is already being finished on the server: do not charge again.
        showNotice(t('subscription.paywall.planChangeInProgress'), { kind: 'info' });
        return;
      }
      await seedAccountEmail();
      const result = await purchasePackage(plan.rcPackage, { customerEmail: email });
      if (!result.success) {
        // Nothing was paid, so nothing changes (promotional access stays).
        if (!result.cancelled) {
          showNotice(t('subscription.paywall.subscriptionFailed', { error: result.error }), { kind: 'error' });
        }
        return;
      }
      void seedAccountEmail();
      // Paid: never offer this switch again this session, even if a refresh is stale.
      setPromoSwitchDone(true);
      setProcessingMessage(t('subscription.paywall.endingPromo'));
      await finishPromoSwitch(plan);
    } catch (err) {
      console.error('Promo switch error:', err);
      showNotice(t('subscription.paywall.purchaseError'), { kind: 'error' });
    } finally {
      try {
        // Keep the page locked until fresh data is in, so stale data cannot re-enable checkout.
        await Promise.all([refetchSubscription(), refetchUserDetails()]);
      } finally {
        setPurchasing(false);
        setProcessingMessage('');
      }
    }
  };

  const openBillingPortal = () => {
    const url = webElite?.managementURL;
    if (!url) return;
    const portal = window.open(url, '_blank');
    if (portal) {
      portal.opener = null;
    } else {
      window.location.assign(url);
    }
  };

  const handleChoosePlan = async (plan) => {
    if (purchasing) {
      return;
    }

    if (pendingPurchasePlan && plan?.id !== 'free') {
      // Paid already; the only safe purchase-side action is to look at the server again, never to
      // pay twice. Going Free stays available at any time.
      showNotice(t('subscription.paywall.subscriptionPendingRefresh'), { kind: 'info' });
      return;
    }

    if (isLowerThanCurrent(plan)) {
      if (isExistingPaidPlan(plan)) {
        setPromoEndPlan(plan);
      } else if (isDowngradeTo(plan) && subscriptionData?.has_canceled !== true) {
        setShowCancelConfirm(true);
      } else if (plan.id === 'advanced' && canScheduleDowngrade) {
        setShowScheduleConfirm(true);
      } else if (plan.id === 'advanced' && canSwitchFromPromo && plan.rcPackage) {
        setPromoSwitchPlan(plan);
      }
      return;
    }

    if (plan?.id === 'free') {
      await Promise.all([refetchSubscription(), refetchUserDetails()]);
      return;
    }

    if (!plan?.rcPackage) {
      return;
    }

    // Promotional access never stays on top of a paid plan: confirm, pay, then end the promo.
    if (canSwitchFromPromo && plan.id !== tier?.toLowerCase()) {
      setPromoSwitchPlan(plan);
      return;
    }

    try {
      setPurchasing(true);
      setProcessingMessage(t('subscription.paywall.processingPayment'));
      await seedAccountEmail();
      const result = await purchasePackage(plan.rcPackage, { customerEmail: email });

      if (result.success) {
        void seedAccountEmail();
        setProcessingMessage(t('subscription.paywall.paymentSuccessActivating'));

        await finishPurchase(plan);
      } else {
        if (!result.cancelled) {
          showNotice(t('subscription.paywall.subscriptionFailed', { error: result.error }), { kind: 'error' });
        }
      }
    } catch (err) {
      console.error('Purchase error:', err);
      showNotice(t('subscription.paywall.purchaseError'), { kind: 'error' });
    } finally {
      setPurchasing(false);
      setProcessingMessage('');
    }
  };

  const isPaidTier = tier && tier !== 'free' && tier !== 'ifpa';
  const isAlreadyCanceled = subscriptionData?.has_canceled === true;
  const hasActiveSubscription = isPaidTier && !isAlreadyCanceled;
  const isPromoSubscription = subscriptionData?.is_promotional === true;

  const formatExpirationDate = (timestamp) => {
    if (!timestamp) return '';
    const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);
    return date.toLocaleDateString(i18n.resolvedLanguage || 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleCancelSubscription = async () => {
    if (subscriptionData?.has_canceled === true) {
      setShowCancelConfirm(false);
      return;
    }

    try {
      // For an account with a promo AND a paid plan the server ends the promo now and cancels the
      // paid plan at period end in one request (the paid plan is untouched if the promo cannot end).
      setProcessingMessage(t('subscription.paywall.cancelling'));
      const result = await cancelSubscriptionMutation.mutateAsync();

      if (result?.status || result?.success) {
        const isPromoResult = result?.immediate === true || result?.is_promotional === true;
        const expiresAt = result?.expires_at;
        const expirationDate = expiresAt ? formatExpirationDate(expiresAt) : '';

        setProcessingMessage('');
        setShowCancelConfirm(false);

        if (isPromoResult) {
          showNotice(t('subscription.paywall.cancellationSuccessPromo'), { kind: 'success' });
        } else if (expirationDate) {
          showNotice(t('subscription.paywall.cancellationSuccess', { date: expirationDate }), { kind: 'success' });
        } else {
          showNotice(t('subscription.paywall.cancellationSuccessNoDate'), { kind: 'success' });
        }

        await refetchSubscription();
        await refetchUserDetails();
      }
    } catch (err) {
      console.error('Cancellation error:', err);
      setProcessingMessage('');
      setShowCancelConfirm(false);
      const errorMessage = err?.message || err?.response?.data?.message || 'Unknown error';
      // axios reports "Network Error" when no response was received at all, so the
      // cancellation may or may not have gone through.
      showNotice(
        errorMessage === 'Network Error'
          ? t('subscription.paywall.cancellationNetworkError')
          : t('subscription.paywall.cancellationError', { error: errorMessage }),
        { kind: 'error' }
      );
      // The plan may have changed (e.g. a payment just landed) or the cancel may have gone
      // through without a reply, so show the current state rather than a stale one.
      try {
        await Promise.all([refetchSubscription(), refetchUserDetails()]);
      } catch (refreshErr) {
        console.warn('Refresh after failed cancellation failed:', refreshErr?.message);
      }
    }
  };

  const noticeDialog = <PaywallNotice notice={notice} onClose={() => setNotice(null)} />;

  if (loading) {
    return <PageSkeleton shape="plans" />;
  }

  if (purchasing || processingMessage || cancelSubscriptionMutation.isPending) {
    let displayMessage = t('subscription.paywall.processing');
    if (cancelSubscriptionMutation.isPending) {
      displayMessage = t('subscription.paywall.cancelling');
    } else if (processingMessage) {
      displayMessage = processingMessage;
    }

    return (
        <div className="w-full h-full flex items-center justify-center bg-[#F0F5F2] bg-dark-app">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bloom-green mx-auto mb-4"></div>
            <p className="font-['Inter'] font-normal text-[16px] text-[#828382] dark:text-[#8b949e]">
              {displayMessage}
            </p>
          </div>
          {noticeDialog}
        </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-[#F0F5F2] bg-dark-app">
        <div className="text-center max-w-[500px] mx-auto px-[24px]">
          <p className="font-['Inter'] font-normal text-[16px] text-[#ef4444] dark:text-[#ff6b6b] mb-4">
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-[24px] py-[12px] bg-bloom-green text-white rounded-[6px] font-['Inter'] font-medium text-[14px] hover:bg-bloom-green-hover transition-colors"
          >
            {t('subscription.paywall.retry')}
          </button>
        </div>
      </div>
    );
  }

  const resolvePackagePlanId = (pkg) => {
    const id = (pkg.identifier || '').toLowerCase();
    const productId = (pkg.product?.identifier || pkg.rcBillingProduct?.identifier || '').toLowerCase();
    const combined = `${id} ${productId}`;
    if (combined.includes('advanced')) return 'advanced';
    if (combined.includes('elite')) return 'elite';
    return null;
  };

  const planOrder = ['free', 'advanced', 'elite'];
  const plansMetadata = getOfferingPlansMetadata(offerings);
  const partnerTags = [
    t('subscription.features.multipleUsers'),
    t('subscription.features.customFunctionality'),
    t('subscription.features.accountManager'),
    t('subscription.features.personalizedConfiguration'),
  ];

  const normalizePlanFeatures = (rawFeatures = []) => rawFeatures
    .map((feature, index) => {
      const text = resolveFeatureText(feature, i18n.language);
      if (!text) return null;

      if (typeof feature === 'object' && feature !== null) {
        return {
          id: feature.id || feature.name || feature.label || `feature-${index}`,
          text,
        };
      }

      return {
        id: `feature-${index}`,
        text,
      };
    })
    .filter(Boolean);

  const paidPlans = offerings?.availablePackages
    ?.filter((pkg) => resolvePackagePlanId(pkg) !== null)
    ?.map((pkg) => {
      const planId = resolvePackagePlanId(pkg);
      const metadata = getPlanMetadata(planId);
      const planFeatures = plansMetadata?.[planId]?.features;

      if (!metadata || !Array.isArray(planFeatures) || planFeatures.length === 0) {
        return null;
      }

      const productPrice = pkg.product?.priceString || pkg.rcBillingProduct?.currentPrice?.formattedPrice;
      const planName = planId.charAt(0).toUpperCase() + planId.slice(1);
      const features = normalizePlanFeatures(planFeatures);

      return {
        id: planId,
        name: planName,
        price: productPrice,
        period: metadata.period,
        features,
        cta: t('subscription.paywall.choosePlan', { plan: planName }),
        highlighted: metadata.highlighted,
        rcPackage: pkg,
      };
    })
    .filter(Boolean) || [];

  const freePlanFeatures = normalizePlanFeatures(plansMetadata?.free?.features || []);
  const freePlan = freePlanFeatures.length > 0
    ? {
      id: 'free',
      name: 'Free',
      price: '$0',
      period: null,
      features: freePlanFeatures,
      cta: t('subscription.paywall.getStarted'),
      highlighted: false,
      rcPackage: null,
    }
    : null;

  const plans = [freePlan, ...paidPlans]
    .filter(Boolean)
    .sort((a, b) => planOrder.indexOf(a.id) - planOrder.indexOf(b.id));

  return (
    <div className="w-full min-h-screen overflow-y-auto bg-[#F0F5F2] bg-dark-app">
      {noticeDialog}
      <div className="w-full pt-[24px] pb-[32px] px-[16px] md:px-[32px]">
        <div className="mx-auto w-full min-w-0 max-w-[1248px]">
        <h1 className="font-['Inter'] font-semibold text-[22px] leading-tight text-center text-[#282A29] dark:text-[#f0f5f2] mb-[24px]">
          {t('subscription.paywall.title')}
        </h1>

        {plans.length === 0 ? (
          <div className="text-center py-[48px]">
            <p className="font-['Inter'] font-normal text-[16px] text-[#828382] dark:text-[#5d625f]">
              {t('subscription.paywall.noPlansAvailable')}
            </p>
          </div>
        ) : (
          <>
            {pendingChange && (
              <div className="mb-[24px] flex flex-wrap items-center justify-between gap-[12px] p-[16px] bg-[#fef3c7] dark:bg-[#78350f] border border-[#fbbf24] dark:border-[#f59e0b] rounded-[12px]">
                <p className="font-['Inter'] font-normal text-[14px] text-[#92400e] dark:text-[#fbbf24]">
                  {t(
                    TIER_RANK[pendingChange.tier] !== undefined
                      ? 'subscription.paywall.pendingChangeBanner'
                      : 'subscription.paywall.pendingChangeBannerGeneric',
                    {
                      plan: capitalize(pendingChange.tier),
                      currentPlan: capitalize(tier),
                      date: formatExpirationDate(pendingChange.effective_at || periodEnd),
                    }
                  )}
                </p>
                {webElite?.managementURL && (
                  <button
                    type="button"
                    onClick={openBillingPortal}
                    className="px-[16px] py-[8px] border border-[#92400e] dark:border-[#fbbf24] rounded-[8px] font-['Inter'] font-medium text-[14px] text-[#92400e] dark:text-[#fbbf24] hover:bg-[#92400e]/5 transition-colors"
                  >
                    {t('subscription.paywall.keepPlan', { plan: capitalize(tier) })}
                  </button>
                )}
              </div>
            )}

            {subscriptionData?.has_canceled && subscriptionData?.expires_date && (
              <div className="mb-[24px] p-[16px] bg-[#fef3c7] dark:bg-[#78350f] border border-[#fbbf24] dark:border-[#f59e0b] rounded-[12px]">
                <p className="font-['Inter'] font-normal text-[14px] text-[#92400e] dark:text-[#fbbf24]">
                  {t('subscription.paywall.cancellationInfo', {
                    date: formatExpirationDate(subscriptionData.expires_date)
                  })}
                </p>
              </div>
            )}

            <div className="flex flex-wrap justify-center gap-[24px] mb-[32px] items-stretch mx-auto max-w-[1248px]">
              {plans.map((plan) => {
                const isCurrentPlan = tier?.toLowerCase() === plan.id;

                return (
                  <PaywallCard
                    key={plan.id}
                    plan={plan}
                    isCurrentPlan={isCurrentPlan}
                    isDowngrade={isLowerThanCurrent(plan)}
                    downgradeDisabled={isDowngradeUnavailable(plan)}
                    lockedLabel={getLockedLabel(plan)}
                    onChoose={handleChoosePlan}
                    purchasing={purchasing}
                  />
                );
              })}
            </div>

            <div className="mb-[32px] bg-white dark:bg-[#1d1d1d] rounded-[12px] border border-[#E8EBE9] dark:border-[#30363d] p-[16px] sm:p-[28px] md:p-[32px] shadow-[0_1px_2px_rgba(16,24,40,0.06),0_12px_32px_-12px_rgba(16,24,40,0.16)] dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/10">
              <div className="flex flex-col items-center gap-y-[20px] text-center">
                <div className="min-w-0">
                  <h2 className="font-['Inter'] font-semibold text-[20px] text-[#282A29] dark:text-[#f0f5f2] mb-[10px]">
                    {t('subscription.paywall.partner.heading')}
                  </h2>
                  {/* No max-width so the copy stays on one line wherever the card is wide enough */}
                  <p className="font-['Inter'] font-normal text-[15px] text-[#282A29] dark:text-[#c9d1d9] mb-[20px] leading-relaxed">
                    {t('subscription.paywall.partner.body')}
                  </p>

                  <div className="flex flex-wrap justify-center gap-[10px]">
                    {partnerTags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-[14px] py-[8px] rounded-full border border-[#C5C9C7] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d] font-['Inter'] font-normal text-[13px] text-[#282A29] dark:text-[#f0f5f2]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <Link
                  to="/contact?context=partner-sales&actionRequired=1&source=subscription"
                  className="max-w-full text-center inline-flex items-center justify-center px-[28px] py-[10px] rounded-[8px] font-['Inter'] font-medium text-[15px] text-white bg-bloom-green hover:bg-bloom-green-hover transition-colors"
                >
                  {t('subscription.paywall.partner.contact')}
                </Link>
              </div>
            </div>

            <div className="flex flex-wrap justify-center items-stretch gap-[24px] mb-[32px]">
              <div className="flex flex-1 basis-[280px] min-w-0 max-w-[600px]">
                <PromoCodeInput onSuccess={async () => {
                  await Promise.all([refetchSubscription(), refetchUserDetails()]);
                }} />
              </div>

              {hasActiveSubscription ? (
                <div className="flex-1 basis-[280px] min-w-0 max-w-[600px] bg-white dark:bg-[#1d1d1d] border border-[#E8EBE9] dark:border-[#30363d] rounded-[12px] p-[16px] sm:p-[28px] flex flex-col shadow-[0_1px_2px_rgba(16,24,40,0.06),0_12px_32px_-12px_rgba(16,24,40,0.16)] dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/10">
                  <div className="mb-[20px] text-center">
                    <h3 className="font-['Inter'] font-semibold text-[18px] text-[#282A29] dark:text-[#f0f5f2] mb-[6px]">
                      {t('subscription.paywall.cancelSection.title')}
                    </h3>
                    <p className="font-['Inter'] text-[14px] text-[#828382] dark:text-[#8b949e] leading-relaxed">
                      {t('subscription.paywall.cancelSection.description')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCancelConfirm(true)}
                    disabled={cancelSubscriptionMutation.isPending || isLoadingSubscription}
                    className="w-full border border-[#ef4444] dark:border-[#ff6b6b] rounded-[8px] px-[24px] py-[12px] text-[#ef4444] dark:text-[#ff6b6b] font-['Inter'] font-medium text-[15px] bg-transparent hover:bg-[#ef4444]/5 dark:hover:bg-[#ff6b6b]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('subscription.paywall.cancelSubscription')}
                  </button>
                </div>
              ) : null}
            </div>

            {showScheduleConfirm && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="schedule-downgrade-title"
                  className="bg-white dark:bg-[#282a29] rounded-[12px] p-[24px] max-w-[500px] w-full max-h-[90vh] overflow-y-auto border border-[#E8EBE9] dark:border-[#5d625f]"
                >
                  <h3 id="schedule-downgrade-title" className="font-['Inter'] font-semibold text-[20px] text-[#282A29] dark:text-[#f0f5f2] mb-[16px]">
                    {t('subscription.paywall.scheduleDowngradeTitle', { plan: 'Advanced' })}
                  </h3>
                  <p className="font-['Inter'] font-normal text-[16px] text-[#828382] dark:text-[#8b949e] mb-[24px]">
                    {t('subscription.paywall.scheduleDowngradeMessage', {
                      currentPlan: capitalize(tier),
                      plan: 'Advanced',
                      price: plans.find((p) => p.id === 'advanced')?.price || '',
                      date: formatExpirationDate(periodEnd),
                    })}
                  </p>
                  <div className="flex flex-wrap gap-[12px] justify-end">
                    <button
                      autoFocus
                      onClick={() => setShowScheduleConfirm(false)}
                      className="min-w-0 max-w-full [overflow-wrap:anywhere] px-[24px] py-[10px] border border-[#E8EBE9] dark:border-[#5d625f] rounded-[8px] font-['Inter'] font-medium text-[14px] text-[#282A29] dark:text-[#f0f5f2] hover:bg-[#F0F5F2] dark:hover:bg-[#1d1d1d] transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={() => {
                        setShowScheduleConfirm(false);
                        openBillingPortal();
                      }}
                      className="min-w-0 max-w-full [overflow-wrap:anywhere] px-[24px] py-[10px] bg-bloom-green rounded-[8px] font-['Inter'] font-medium text-[14px] text-white hover:bg-bloom-green-hover transition-colors"
                    >
                      {t('subscription.paywall.continueToBillingPortal')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {promoEndPlan && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="promo-end-title"
                  className="bg-white dark:bg-[#282a29] rounded-[12px] p-[24px] max-w-[500px] w-full max-h-[90vh] overflow-y-auto border border-[#E8EBE9] dark:border-[#5d625f]"
                >
                  <h3 id="promo-end-title" className="font-['Inter'] font-semibold text-[20px] text-[#282A29] dark:text-[#f0f5f2] mb-[16px]">
                    {t('subscription.paywall.promoEndTitle', { plan: promoEndPlan.name })}
                  </h3>
                  <p className="font-['Inter'] font-normal text-[16px] text-[#828382] dark:text-[#8b949e] mb-[24px]">
                    {t('subscription.paywall.promoEndMessage', {
                      plan: promoEndPlan.name,
                      currentPlan: capitalize(tier),
                    })}
                  </p>
                  <div className="flex flex-wrap gap-[12px] justify-end">
                    <button
                      autoFocus
                      onClick={() => setPromoEndPlan(null)}
                      className="min-w-0 max-w-full [overflow-wrap:anywhere] px-[24px] py-[10px] border border-[#E8EBE9] dark:border-[#5d625f] rounded-[8px] font-['Inter'] font-medium text-[14px] text-[#282A29] dark:text-[#f0f5f2] hover:bg-[#F0F5F2] dark:hover:bg-[#1d1d1d] transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={handleEndPromoKeepPaid}
                      className="min-w-0 max-w-full [overflow-wrap:anywhere] px-[24px] py-[10px] bg-bloom-green rounded-[8px] font-['Inter'] font-medium text-[14px] text-white hover:bg-bloom-green-hover transition-colors"
                    >
                      {t('subscription.paywall.promoEndConfirm', { plan: promoEndPlan.name })}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {promoSwitchPlan && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="promo-switch-title"
                  className="bg-white dark:bg-[#282a29] rounded-[12px] p-[24px] max-w-[500px] w-full max-h-[90vh] overflow-y-auto border border-[#E8EBE9] dark:border-[#5d625f]"
                >
                  <h3 id="promo-switch-title" className="font-['Inter'] font-semibold text-[20px] text-[#282A29] dark:text-[#f0f5f2] mb-[16px]">
                    {t('subscription.paywall.promoSwitchTitle', { plan: promoSwitchPlan.name })}
                  </h3>
                  <p className="font-['Inter'] font-normal text-[16px] text-[#828382] dark:text-[#8b949e] mb-[24px]">
                    {t('subscription.paywall.promoSwitchMessage', {
                      plan: promoSwitchPlan.name,
                      price: `${promoSwitchPlan.price || ''}${promoSwitchPlan.period || ''}`,
                      currentPlan: capitalize(tier),
                    })}
                  </p>
                  <div className="flex flex-wrap gap-[12px] justify-end">
                    <button
                      autoFocus
                      onClick={() => setPromoSwitchPlan(null)}
                      className="min-w-0 max-w-full [overflow-wrap:anywhere] px-[24px] py-[10px] border border-[#E8EBE9] dark:border-[#5d625f] rounded-[8px] font-['Inter'] font-medium text-[14px] text-[#282A29] dark:text-[#f0f5f2] hover:bg-[#F0F5F2] dark:hover:bg-[#1d1d1d] transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={handlePromoSwitch}
                      className="min-w-0 max-w-full [overflow-wrap:anywhere] px-[24px] py-[10px] bg-bloom-green rounded-[8px] font-['Inter'] font-medium text-[14px] text-white hover:bg-bloom-green-hover transition-colors"
                    >
                      {t('subscription.paywall.promoSwitchConfirm', { plan: promoSwitchPlan.name })}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showCancelConfirm && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-[#282a29] rounded-[12px] p-[24px] max-w-[500px] w-full max-h-[90vh] overflow-y-auto border border-[#E8EBE9] dark:border-[#5d625f]">
                  <h3 className="font-['Inter'] font-semibold text-[20px] text-[#282A29] dark:text-[#f0f5f2] mb-[16px]">
                    {t(isPromoSubscription && !webSubscription
                      ? 'subscription.paywall.cancelConfirmTitlePromo'
                      : 'subscription.paywall.cancelConfirmTitle')}
                  </h3>
                  <p className="font-['Inter'] font-normal text-[16px] text-[#828382] dark:text-[#8b949e] mb-[24px]">
                    {isPromoSubscription && webSubscription
                      ? t('subscription.paywall.cancelConfirmMessagePromoAndPaid', {
                          plan: capitalize(webSubscription.tier),
                          date: formatExpirationDate(webSubscription.current_period_ends_at),
                        })
                      : t(isPromoSubscription
                        ? 'subscription.paywall.cancelConfirmMessagePromo'
                        : 'subscription.paywall.cancelConfirmMessage')}
                  </p>
                  <div className="flex flex-wrap gap-[12px] justify-end">
                    <button
                      onClick={() => setShowCancelConfirm(false)}
                      disabled={cancelSubscriptionMutation.isPending}
                      className="min-w-0 max-w-full [overflow-wrap:anywhere] px-[24px] py-[10px] border border-[#E8EBE9] dark:border-[#5d625f] rounded-[8px] font-['Inter'] font-medium text-[14px] text-[#282A29] dark:text-[#f0f5f2] hover:bg-[#F0F5F2] dark:hover:bg-[#1d1d1d] transition-colors disabled:opacity-50"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancelSubscriptionMutation.isPending}
                      className="min-w-0 max-w-full [overflow-wrap:anywhere] px-[24px] py-[10px] bg-[#ef4444] dark:bg-[#ff6b6b] rounded-[8px] font-['Inter'] font-medium text-[14px] text-white hover:bg-[#dc2626] dark:hover:bg-[#ff5252] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {cancelSubscriptionMutation.isPending 
                        ? t('subscription.paywall.cancelling') 
                        : t('subscription.paywall.confirmCancel')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-center gap-x-[32px] gap-y-[8px] pt-[8px] pb-[24px]">
              <a
                href="/terms"
                className="font-['Inter'] font-normal text-[14px] text-[#828382] dark:text-[#5d625f] hover:text-bloom-green transition-colors"
              >
                {t('subscription.paywall.termsAndConditions')}
              </a>
              <a
                href="/privacy"
                className="font-['Inter'] font-normal text-[14px] text-[#828382] dark:text-[#5d625f] hover:text-bloom-green transition-colors"
              >
                {t('subscription.paywall.privacyPolicy')}
              </a>
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default Paywall;
